import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { validatePassword, validatePasswordMatch } from '../utils/validation';
import { trackEvent } from '../utils/analytics';
import { ErrorMessage, SuccessMessage, Loading } from './common';
import './Auth.css';

function ResetPassword() {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      trackEvent('reset_password_failed', { reason: 'invalid_link' });
      setError(t('auth.resetPassword.invalidLink'));
    }
  }, [searchParams, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    trackEvent('reset_password_submit');
    setError('');
    setSuccess('');

    const matchError = validatePasswordMatch(password, confirmPassword, t);
    if (matchError) {
      trackEvent('reset_password_failed', { reason: 'password_mismatch' });
      setError(matchError);
      return;
    }

    const passwordError = validatePassword(password, t);
    if (passwordError) {
      trackEvent('reset_password_failed', { reason: 'invalid_password' });
      setError(passwordError);
      return;
    }

    setLoading(true);
    const result = await resetPassword(token, password);

    if (result.success) {
      trackEvent('reset_password_success');
      setSuccess(result.message);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } else {
      trackEvent('reset_password_failed', { reason: 'server_error' });
      setError(result.error);
    }

    setLoading(false);
  };

  if (!token && !error) {
    return (
      <div className="auth-container">
        <Loading />
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>{t('auth.resetPassword.title')}</h2>

        <ErrorMessage message={error} />
        <SuccessMessage message={success} />

        {!success && token && (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="password">{t('auth.resetPassword.newPassword')}</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder={t('auth.resetPassword.passwordPlaceholder')}
                minLength="6"
              />
              <small className="password-hint">
                {t('auth.resetPassword.passwordHint')}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">{t('auth.resetPassword.confirmPassword')}</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                minLength="6"
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('auth.resetPassword.submitting') : t('auth.resetPassword.submit')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
